import { useMutation } from '@apollo/client'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField as MuiTextField,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AddIcon from '@mui/icons-material/Add'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, TextField } from 'ui/baseComponents'
import JsonEditor from 'ui/components/JsonEditor'
import { CRITERIA_TYPES } from '../../consts'
import { UPDATE_TAG } from '../../graphql/tagMutations'
import { LIST_TAGS, useTagList } from '../../graphql/tagQueries'

const CRITERIA_CARD_HEIGHT = '4.5rem'

const emptyStateBoxStyles = {
  px: 3,
  py: 2.5,
  border: 1,
  borderRadius: 1,
  mb: 1.5,
  display: 'flex',
  alignItems: 'center',
  height: CRITERIA_CARD_HEIGHT,
  boxSizing: 'border-box',
}

const COMMON_OPENSEARCH_QUERIES = [
  {
    name: 'Usage or Research Restricted',
    index: 'patient_metadata',
    body: {
      query: {
        bool: {
          must: [
            {
              match_phrase: {
                patient_type: 'USAGE_RESTRICTED',
              },
            },
            {
              match_phrase: {
                org_tags: 'RESEARCH_RESTRICTED',
              },
            },
          ],
        },
      },
    },
  },
  {
    name: 'Example: Low Daily Availability (6M)',
    index: 'daily_metrics',
    body: {
      query: { range: { date: { gte: 'now-6M/d' } } },
      aggs: {
        patients: {
          terms: { field: 'patient_id', size: 10000 },
          aggs: {
            avg_availability: {
              avg: { field: 'mm4pd_availability_sum' },
            },
            low_availability_filter: {
              bucket_selector: {
                buckets_path: { avgAvailability: 'avg_availability' },
                script: 'params.avgAvailability < 6',
              },
            },
          },
        },
      },
    },
  },
]

const CriteriaHelpText = ({ type }) => {
  const content = {
    org: (
      <>
        <Typography variant="body2" gutterBottom>
          Target patients in specific organizations.
        </Typography>
        <Typography variant="subtitle2" component="h3" gutterBottom>
          How to use:
        </Typography>
        <Box component="ul" sx={{ mt: 0 }}>
          <Typography component="li" variant="body2">
            Enter one organization ID per line
          </Typography>
        </Box>
      </>
    ),
    patientIds: (
      <>
        <Typography variant="body2" gutterBottom>
          Target specific patients by their IDs.{' '}
          <strong>Best for small lists</strong> and quick tests.
        </Typography>

        <Typography variant="subtitle2" component="h3" gutterBottom>
          When to use:
        </Typography>
        <Box component="ul" sx={{ mt: 0 }}>
          <Typography component="li" variant="body2">
            Quick testing with small patient subsets (&lt;100 patients)
          </Typography>
          <Typography component="li" variant="body2">
            Adding specific patients to a campaign
          </Typography>
          <Typography component="li" variant="body2">
            Removing patients from targeting
          </Typography>
        </Box>

        <Typography variant="subtitle2" component="h3" gutterBottom>
          How to use:
        </Typography>
        <Box component="ul" sx={{ mt: 0 }}>
          <Typography component="li" variant="body2">
            Enter patient IDs one per line, or
          </Typography>
          <Typography component="li" variant="body2">
            Upload a CSV file with one patient ID per row
          </Typography>
          <Typography component="li" variant="body2">
            Invalid patient IDs are automatically filtered out
          </Typography>
        </Box>
      </>
    ),
    tag: (
      <>
        <Typography variant="body2" gutterBottom>
          Reference another tag's patient list.{' '}
          <strong>Best for reusing static, manually-curated cohorts</strong>{' '}
          across multiple campaigns (100s to 1000s of patients).
        </Typography>

        <Typography variant="subtitle2" component="h3" gutterBottom>
          Benefits:
        </Typography>
        <Box component="ul" sx={{ mt: 0 }}>
          <Typography component="li" variant="body2">
            Reusable across multiple campaigns
          </Typography>
          <Typography component="li" variant="body2">
            Named and discoverable
          </Typography>
          <Typography component="li" variant="body2">
            Single source of truth for patient cohorts
          </Typography>
          <Typography component="li" variant="body2">
            Better performance for large patient lists
          </Typography>
        </Box>

        <Typography variant="subtitle2" component="h3" gutterBottom>
          Requirements:
        </Typography>
        <Box component="ul" sx={{ mt: 0 }}>
          <Typography component="li" variant="body2">
            Source tag must be manually populated (cannot have its own criteria)
          </Typography>
        </Box>

        <Typography variant="subtitle2" component="h3" gutterBottom>
          Example Use Case:
        </Typography>
        <Box component="ol" sx={{ mt: 0 }}>
          <Typography component="li" variant="body2">
            Create a tag named "Q4-2024-Cohort"
          </Typography>
          <Typography component="li" variant="body2">
            Manually tag 2000 patients to it
          </Typography>
          <Typography component="li" variant="body2">
            Create campaign tags that reference "Q4-2024-Cohort" with different
            exclusions
          </Typography>
          <Typography component="li" variant="body2">
            Reuse the same cohort across email, push, and SMS campaigns
          </Typography>
        </Box>
      </>
    ),
    salesforce: (
      <>
        <Typography variant="body2" gutterBottom>
          Query patients from Salesforce using SOQL. Powerful for targeting
          based on CRM data.
        </Typography>

        <Typography variant="subtitle2" component="h3" gutterBottom>
          How to use:
        </Typography>
        <Box component="ul" sx={{ mt: 0 }}>
          <Typography component="li" variant="body2">
            Enter a SOQL query that selects <code>Strive_Patient_ID__c</code>
          </Typography>
        </Box>

        <Typography variant="subtitle2" component="h3" gutterBottom>
          Example:
        </Typography>
        <Typography
          variant="body2"
          component="pre"
          sx={{
            fontFamily: 'monospace',
            backgroundColor: 'grey.100',
            p: 1,
            borderRadius: 1,
          }}
        >
          SELECT Strive_Patient_ID__c FROM Lead WHERE Status = 'Active'
        </Typography>
      </>
    ),
    openSearch: (
      <>
        <Typography variant="body2" gutterBottom>
          Complex queries against patient data indices. Supports both simple
          document queries and aggregation-based criteria.
        </Typography>

        <Typography variant="subtitle2" component="h3" gutterBottom>
          How to use:
        </Typography>
        <Box component="ul" sx={{ mt: 0 }}>
          <Typography component="li" variant="body2">
            Specify the index name (e.g., <code>daily_metrics</code>,{' '}
            <code>patient_metadata</code>)
          </Typography>
          <Typography component="li" variant="body2">
            Provide a request body as JSON containing <code>query</code> and
            optionally <code>aggs</code>
          </Typography>
        </Box>

        <Typography variant="subtitle2" component="h3" gutterBottom>
          Example:
        </Typography>
        <Typography
          variant="body2"
          component="pre"
          sx={{
            fontFamily: 'monospace',
            backgroundColor: 'grey.100',
            p: 1,
            borderRadius: 1,
          }}
        >
          {`{"query": {"term": {"status": "active"}}}`}
        </Typography>
      </>
    ),
    jotform: (
      <>
        <Typography variant="body2" gutterBottom>
          Target patients from a Jotform form submission.
        </Typography>

        <Typography variant="subtitle2" component="h3" gutterBottom>
          How to use:
        </Typography>
        <Box component="ul" sx={{ mt: 0 }}>
          <Typography component="li" variant="body2">
            Enter the Jotform Form ID
          </Typography>
        </Box>
      </>
    ),
  }

  return content[type]
}

const getCriteriaTypeLabel = (criteria) => {
  if (criteria.org) return 'Org Membership'
  if (criteria.patientIds) return 'Patient IDs'
  if (criteria.tag) return 'Tag Reference'
  if (criteria.salesforce) return 'Salesforce Query'
  if (criteria.openSearch) return 'OpenSearch Query'
  if (criteria.jotform) return 'Jotform'
  return 'Unknown'
}

const getCriteriaDetails = (criteria, allTags = []) => {
  if (criteria.org) {
    const count = criteria.org.orgIds.length
    return `${count} org${count !== 1 ? 's' : ''}`
  }
  if (criteria.patientIds) {
    const count = criteria.patientIds.patientIds.length
    return `${count} patient${count !== 1 ? 's' : ''}`
  }
  if (criteria.tag) {
    const tag = allTags.find((t) => t.id === criteria.tag.tagId)
    return tag ? `${tag.tag} (${tag.id})` : `Tag ID: ${criteria.tag.tagId}`
  }
  if (criteria.salesforce) {
    const q = criteria.salesforce.query
    return q.length > 50 ? `${q.slice(0, 50)}...` : q
  }
  if (criteria.openSearch) {
    return `Index: ${criteria.openSearch.index}`
  }
  if (criteria.jotform) {
    return `Form ID: ${criteria.jotform.formId}`
  }
  return ''
}

const CriteriaCard = ({ criteria, onRemove, onEdit, listType, allTags }) => {
  const [expanded, setExpanded] = useState(false)
  const typeLabel = getCriteriaTypeLabel(criteria)
  const details = getCriteriaDetails(criteria, allTags)

  const backgroundColor = listType === 'include' ? 'success.50' : 'error.50'
  const borderColor = listType === 'include' ? 'success.light' : 'error.light'

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{
        mb: 1.5,
        border: 1,
        borderColor,
        '&:before': { display: 'none' },
        boxShadow: 1,
        '&:not(.Mui-expanded)': {
          height: CRITERIA_CARD_HEIGHT,
          boxSizing: 'border-box',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor,
          px: 3,
          py: 2.5,
          minHeight: '0 !important',
          height: '100%',
          '& .MuiAccordionSummary-content': {
            margin: '0 !important',
            display: 'flex',
            alignItems: 'center',
          },
          '&:hover': {
            backgroundColor:
              listType === 'include' ? 'success.100' : 'error.100',
          },
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {typeLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {details}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ p: 1 }}>
          {criteria.org && (
            <Box>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Organization IDs:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  backgroundColor: 'grey.100',
                  p: 1,
                  borderRadius: 1,
                }}
              >
                {criteria.org.orgIds.join('\n')}
              </Typography>
            </Box>
          )}
          {criteria.patientIds && (
            <Box>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Patient IDs:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  backgroundColor: 'grey.100',
                  p: 1,
                  borderRadius: 1,
                  maxHeight: 200,
                  overflow: 'auto',
                }}
              >
                {criteria.patientIds.patientIds.join('\n')}
              </Typography>
            </Box>
          )}
          {criteria.tag && (
            <Box>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Referenced Tag:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  backgroundColor: 'grey.100',
                  p: 1,
                  borderRadius: 1,
                }}
              >
                {(() => {
                  const tag = allTags.find((t) => t.id === criteria.tag.tagId)
                  return tag ? `${tag.tag} (${tag.id})` : criteria.tag.tagId
                })()}
              </Typography>
            </Box>
          )}
          {criteria.salesforce && (
            <Box>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                SOQL Query:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  backgroundColor: 'grey.100',
                  p: 1,
                  borderRadius: 1,
                }}
              >
                {criteria.salesforce.query}
              </Typography>
            </Box>
          )}
          {criteria.openSearch && (
            <Box>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Index: {criteria.openSearch.index}
              </Typography>
              {criteria.openSearch.query && (
                <>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    gutterBottom
                    sx={{ mt: 1 }}
                  >
                    Query:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      backgroundColor: 'grey.100',
                      p: 1,
                      borderRadius: 1,
                    }}
                  >
                    {JSON.stringify(criteria.openSearch.query, null, 2)}
                  </Typography>
                </>
              )}
              {criteria.openSearch.aggs && (
                <>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    gutterBottom
                    sx={{ mt: 1 }}
                  >
                    Aggregations:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      backgroundColor: 'grey.100',
                      p: 1,
                      borderRadius: 1,
                    }}
                  >
                    {JSON.stringify(criteria.openSearch.aggs, null, 2)}
                  </Typography>
                </>
              )}
            </Box>
          )}
          {criteria.jotform && (
            <Box>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Form ID:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  backgroundColor: 'grey.100',
                  p: 1,
                  borderRadius: 1,
                }}
              >
                {criteria.jotform.formId}
              </Typography>
            </Box>
          )}
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}
          >
            <Button
              type="secondary"
              size="small"
              startIcon={<EditIcon />}
              onClick={onEdit}
            >
              Edit
            </Button>
            <Button
              type="secondary"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={onRemove}
              sx={{
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'error.50',
                },
              }}
            >
              Remove
            </Button>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}

const getCriteriaType = (criteria) => {
  if (criteria.org) return 'org'
  if (criteria.patientIds) return 'patientIds'
  if (criteria.tag) return 'tag'
  if (criteria.salesforce) return 'salesforce'
  if (criteria.openSearch) return 'openSearch'
  if (criteria.jotform) return 'jotform'
  return 'org'
}

const CriteriaFormModal = ({
  open,
  onClose,
  listType,
  onAdd,
  onEdit,
  editingCriteria,
  availableTags,
  currentTagId,
}) => {
  const isEditing = Boolean(editingCriteria)
  const [criteriaType, setCriteriaType] = useState('org')
  const [orgIds, setOrgIds] = useState('')
  const [patientIds, setPatientIds] = useState('')
  const [selectedTagId, setSelectedTagId] = useState('')
  const [salesforceQuery, setSalesforceQuery] = useState('')
  const [osIndex, setOsIndex] = useState('')
  const [osBody, setOsBody] = useState({})
  const [osBodyError, setOsBodyError] = useState(null)
  const [jotformFormId, setJotformFormId] = useState('')
  const fileInputRef = useRef(null)

  useEffect(() => {
    if (!open) return
    if (!editingCriteria) {
      setCriteriaType('org')
      setOrgIds('')
      setPatientIds('')
      setSelectedTagId('')
      setSalesforceQuery('')
      setOsIndex('')
      setOsBody({})
      setOsBodyError(null)
      setJotformFormId('')
      return
    }
    const type = getCriteriaType(editingCriteria)
    setCriteriaType(type)
    if (editingCriteria.org) setOrgIds(editingCriteria.org.orgIds.join('\n'))
    if (editingCriteria.patientIds)
      setPatientIds(editingCriteria.patientIds.patientIds.join('\n'))
    if (editingCriteria.tag) setSelectedTagId(editingCriteria.tag.tagId)
    if (editingCriteria.salesforce)
      setSalesforceQuery(editingCriteria.salesforce.query)
    if (editingCriteria.openSearch) {
      setOsIndex(editingCriteria.openSearch.index)
      const body = {}
      if (editingCriteria.openSearch.query)
        body.query = editingCriteria.openSearch.query
      if (editingCriteria.openSearch.aggs)
        body.aggs = editingCriteria.openSearch.aggs
      setOsBody(body)
      setOsBodyError(null)
    }
    if (editingCriteria.jotform)
      setJotformFormId(editingCriteria.jotform.formId)
  }, [open, editingCriteria])

  const handleCsvUpload = useCallback((event) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target?.result
      if (typeof text !== 'string') return

      const lines = text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)

      const hasHeader = lines.length > 0 && isNaN(lines[0])
      const ids = hasHeader ? lines.slice(1) : lines

      setPatientIds(ids.join('\n'))
    }
    reader.onerror = () => {
      setPatientIds('')
    }
    reader.readAsText(file)

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const buildCriteria = useCallback(() => {
    switch (criteriaType) {
      case 'org': {
        const ids = orgIds
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean)
        if (!ids.length) return null
        return { org: { orgIds: ids } }
      }
      case 'patientIds': {
        const ids = patientIds
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean)
        if (!ids.length) return null
        return { patientIds: { patientIds: ids } }
      }
      case 'tag': {
        if (!selectedTagId) return null
        return { tag: { tagId: selectedTagId } }
      }
      case 'salesforce': {
        if (!salesforceQuery.trim()) return null
        return { salesforce: { query: salesforceQuery.trim() } }
      }
      case 'openSearch': {
        if (!osIndex.trim() || !osBody || osBodyError) return null
        const payload = { index: osIndex.trim() }
        if (osBody.query) payload.query = osBody.query
        if (osBody.aggs) payload.aggs = osBody.aggs
        return { openSearch: payload }
      }
      case 'jotform': {
        if (!jotformFormId.trim()) return null
        return { jotform: { formId: jotformFormId.trim() } }
      }
      default:
        return null
    }
  }, [
    criteriaType,
    orgIds,
    patientIds,
    selectedTagId,
    salesforceQuery,
    osIndex,
    osBody,
    osBodyError,
    jotformFormId,
  ])

  const handleSubmit = () => {
    const criteria = buildCriteria()
    if (!criteria) return
    if (isEditing) {
      onEdit(listType, criteria)
    } else {
      onAdd(listType, criteria)
    }
    onClose()
  }

  const listLabel = listType === 'include' ? 'Include' : 'Exclude'
  const title = isEditing
    ? `Edit ${listLabel} Criteria`
    : `Add ${listLabel} Criteria`
  const buttonColor = listType === 'include' ? 'success.main' : 'error.main'

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          position: 'absolute',
          top: 100,
          m: 0,
        },
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={criteriaType}
              label="Type"
              onChange={(e) => setCriteriaType(e.target.value)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root': {
                      whiteSpace: 'normal',
                      display: 'block',
                      padding: '10px 16px',
                    },
                  },
                },
              }}
            >
              {CRITERIA_TYPES.map((ct) => (
                <MenuItem key={ct.value} value={ct.value}>
                  {ct.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <CriteriaHelpText type={criteriaType} />

          {criteriaType === 'org' && (
            <TextField
              label="Org IDs (one per line)"
              value={orgIds}
              onChange={(e) => setOrgIds(e.target.value)}
              multiline
              rows={3}
              fullWidth
              size="small"
            />
          )}
          {criteriaType === 'patientIds' && (
            <>
              <TextField
                label="Patient IDs (one per line)"
                value={patientIds}
                onChange={(e) => setPatientIds(e.target.value)}
                multiline
                rows={3}
                fullWidth
                size="small"
              />
              <Box>
                <input
                  type="file"
                  accept=".csv"
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                  onChange={handleCsvUpload}
                />
                <Button
                  type="secondary"
                  size="small"
                  startIcon={<UploadFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Upload CSV
                </Button>
              </Box>
            </>
          )}
          {criteriaType === 'tag' && (
            <Autocomplete
              value={
                availableTags.find((tag) => tag.id === selectedTagId) || null
              }
              onChange={(e, newValue) => {
                setSelectedTagId(newValue?.id || '')
              }}
              options={availableTags.filter((tag) => tag.id !== currentTagId)}
              getOptionLabel={(option) => `${option.tag} (${option.id})`}
              renderInput={(params) => (
                <MuiTextField
                  {...params}
                  label="Select Tag"
                  size="small"
                  placeholder="Type to search..."
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          )}
          {criteriaType === 'salesforce' && (
            <TextField
              label="SOQL Query"
              value={salesforceQuery}
              onChange={(e) => setSalesforceQuery(e.target.value)}
              multiline
              rows={3}
              fullWidth
              size="small"
              placeholder="SELECT Strive_Patient_ID__c FROM Lead WHERE Status = 'Active'"
            />
          )}
          {criteriaType === 'openSearch' && (
            <>
              <Box>
                <Typography variant="body2" gutterBottom>
                  <strong>Common Queries:</strong>
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  {COMMON_OPENSEARCH_QUERIES.map((commonQuery) => (
                    <Box
                      key={commonQuery.name}
                      onClick={() => {
                        setOsIndex(commonQuery.index)
                        setOsBody(commonQuery.body)
                      }}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: '8px',
                        border: '2px solid',
                        borderColor: 'grey.400',
                        backgroundColor: 'primary.50',
                        px: 2,
                        py: 0.5,
                        display: 'inline-flex',
                        alignItems: 'center',
                        fontSize: '0.8125rem',
                        '&:hover': {
                          backgroundColor: 'primary.100',
                          borderColor: 'grey.600',
                        },
                      }}
                    >
                      <Typography variant="body2" fontSize="inherit">
                        {commonQuery.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Autocomplete
                freeSolo
                value={osIndex}
                onChange={(e, newValue) => {
                  if (newValue !== null) {
                    setOsIndex(newValue)
                  }
                }}
                onInputChange={(e, newValue) => {
                  setOsIndex(newValue)
                }}
                options={[
                  'daily_metrics',
                  'jotform_submissions',
                  'patient_metadata',
                ]}
                renderInput={(params) => (
                  <MuiTextField
                    {...params}
                    label="Index Name"
                    size="small"
                    placeholder="e.g., patient_metadata"
                  />
                )}
              />
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Request Body JSON (include <code>query</code> and optionally{' '}
                  <code>aggs</code>)
                </Typography>
                <JsonEditor
                  value={osBody}
                  onChange={(json, errors) => {
                    if (
                      typeof json === 'object' &&
                      json !== null &&
                      !Array.isArray(json)
                    ) {
                      setOsBody(json)
                      setOsBodyError(null)
                    } else {
                      setOsBodyError(errors)
                    }
                  }}
                  style={{ minHeight: 200, maxHeight: 400 }}
                />
              </Box>
            </>
          )}
          {criteriaType === 'jotform' && (
            <MuiTextField
              label="Form ID"
              value={jotformFormId}
              onChange={(e) => setJotformFormId(e.target.value)}
              fullWidth
              size="small"
              placeholder="e.g., 123456789012345"
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button type="secondary" size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="primary"
          size="small"
          onClick={handleSubmit}
          sx={{ backgroundColor: buttonColor }}
        >
          {isEditing ? 'Save' : 'Add Criteria'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const TagCriteriaEditor = ({ tag }) => {
  const [includeCriteria, setIncludeCriteria] = useState([])
  const [excludeCriteria, setExcludeCriteria] = useState([])
  const [error, setError] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalListType, setModalListType] = useState('include')
  const [editingIndex, setEditingIndex] = useState(null)
  const [editingCriteria, setEditingCriteria] = useState(null)

  const { data: tagsData } = useTagList()
  const allTags = tagsData?.tags || []

  const [updateTagMutation, { loading: savingCriteria }] = useMutation(
    UPDATE_TAG,
    {
      refetchQueries: [{ query: LIST_TAGS }],
    },
  )

  useEffect(() => {
    setIncludeCriteria(tag.include ? [...tag.include] : [])
    setExcludeCriteria(tag.exclude ? [...tag.exclude] : [])
    setError(null)
  }, [tag])

  const saveCriteria = useCallback(
    async (newInclude, newExclude) => {
      setError(null)
      try {
        await updateTagMutation({
          variables: {
            input: {
              tagId: tag.id,
              include: newInclude,
              exclude: newExclude,
            },
          },
        })
      } catch (err) {
        setError('Failed to update criteria. Please try again.')
      }
    },
    [tag.id, updateTagMutation],
  )

  const handleAdd = useCallback(
    async (listType, criteria) => {
      let newInclude = includeCriteria
      let newExclude = excludeCriteria

      if (listType === 'include') {
        newInclude = [...includeCriteria, criteria]
        setIncludeCriteria(newInclude)
      } else {
        newExclude = [...excludeCriteria, criteria]
        setExcludeCriteria(newExclude)
      }

      await saveCriteria(newInclude, newExclude)
    },
    [includeCriteria, excludeCriteria, saveCriteria],
  )

  const handleEditCriteria = useCallback(
    async (listType, criteria) => {
      let newInclude = includeCriteria
      let newExclude = excludeCriteria

      if (listType === 'include') {
        newInclude = includeCriteria.map((c, i) =>
          i === editingIndex ? criteria : c,
        )
        setIncludeCriteria(newInclude)
      } else {
        newExclude = excludeCriteria.map((c, i) =>
          i === editingIndex ? criteria : c,
        )
        setExcludeCriteria(newExclude)
      }

      setEditingIndex(null)
      setEditingCriteria(null)
      await saveCriteria(newInclude, newExclude)
    },
    [includeCriteria, excludeCriteria, editingIndex, saveCriteria],
  )

  const handleRemoveInclude = useCallback(
    async (index) => {
      const newInclude = includeCriteria.filter((_, i) => i !== index)
      setIncludeCriteria(newInclude)
      await saveCriteria(newInclude, excludeCriteria)
    },
    [includeCriteria, excludeCriteria, saveCriteria],
  )

  const handleRemoveExclude = useCallback(
    async (index) => {
      const newExclude = excludeCriteria.filter((_, i) => i !== index)
      setExcludeCriteria(newExclude)
      await saveCriteria(includeCriteria, newExclude)
    },
    [includeCriteria, excludeCriteria, saveCriteria],
  )

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" paragraph>
          Define which patients should be included or excluded from this tag
          using selection criteria. You can combine multiple criteria types to
          create precise targeting rules.
        </Typography>

        <Typography variant="subtitle2" component="h3" gutterBottom>
          Available Criteria Types:
        </Typography>
        <Box component="ul" sx={{ mt: 0, mb: 2 }}>
          <Typography component="li" variant="body2">
            <strong>Org Membership:</strong> Target patients in specific
            organizations
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Patient IDs:</strong> Target specific patients (best for
            small lists &lt;100)
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Tag Reference:</strong> Reference another tag's patient list
            (best for large cohorts 100s-1000s)
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Salesforce Query:</strong> Query patients from Salesforce
            using SOQL
          </Typography>
          <Typography component="li" variant="body2">
            <strong>OpenSearch Query:</strong> Complex queries against patient
            data
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Jotform:</strong> Target patients from Jotform submissions
          </Typography>
        </Box>

        <Typography variant="subtitle2" component="h3" gutterBottom>
          How It Works:
        </Typography>
        <Box component="ol" sx={{ mt: 0, mb: 2 }}>
          <Typography component="li" variant="body2">
            Click <strong>Add Include Criteria</strong> or{' '}
            <strong>Add Exclude Criteria</strong>
          </Typography>
          <Typography component="li" variant="body2">
            Select a criteria type and fill in the required fields
          </Typography>
          <Typography component="li" variant="body2">
            Criteria are <strong>saved automatically</strong> when added or
            removed
          </Typography>
          <Typography component="li" variant="body2">
            Use the <strong>Patients</strong> panel below to test and execute
            criteria
          </Typography>
        </Box>

        <Typography variant="subtitle2" component="h3" gutterBottom>
          Include vs Exclude:
        </Typography>
        <Box component="ul" sx={{ mt: 0, mb: 2 }}>
          <Typography component="li" variant="body2">
            <strong>Include:</strong> Patients matching any include criteria
            will be added to the tag
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Exclude:</strong> Patients matching any exclude criteria
            will be removed from the tag
          </Typography>
          <Typography component="li" variant="body2">
            Exclusions are applied after inclusions
          </Typography>
        </Box>
      </Box>

      {savingCriteria && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Saving criteria...
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'success.dark' }}>
            Include ({includeCriteria.length})
          </Typography>
          {includeCriteria.length === 0 ? (
            <Box
              sx={{
                ...emptyStateBoxStyles,
                borderColor: 'success.light',
                backgroundColor: 'success.50',
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ width: '100%', textAlign: 'center' }}
              >
                No include criteria
              </Typography>
            </Box>
          ) : (
            includeCriteria.map((c, i) => (
              <CriteriaCard
                key={JSON.stringify(c)}
                criteria={c}
                onRemove={() => handleRemoveInclude(i)}
                onEdit={() => {
                  setEditingIndex(i)
                  setEditingCriteria(c)
                  setModalListType('include')
                  setModalOpen(true)
                }}
                listType="include"
                allTags={allTags}
              />
            ))
          )}
          <Button
            type="secondary"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingIndex(null)
              setEditingCriteria(null)
              setModalListType('include')
              setModalOpen(true)
            }}
            sx={{
              borderColor: 'success.main',
              color: 'success.main',
              '&:hover': {
                borderColor: 'success.dark',
                backgroundColor: 'success.50',
              },
            }}
          >
            Add Include Criteria
          </Button>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'error.dark' }}>
            Exclude ({excludeCriteria.length})
          </Typography>
          {excludeCriteria.length === 0 ? (
            <Box
              sx={{
                ...emptyStateBoxStyles,
                borderColor: 'error.light',
                backgroundColor: 'error.50',
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ width: '100%', textAlign: 'center' }}
              >
                No exclude criteria
              </Typography>
            </Box>
          ) : (
            excludeCriteria.map((c, i) => (
              <CriteriaCard
                key={JSON.stringify(c)}
                criteria={c}
                onRemove={() => handleRemoveExclude(i)}
                onEdit={() => {
                  setEditingIndex(i)
                  setEditingCriteria(c)
                  setModalListType('exclude')
                  setModalOpen(true)
                }}
                listType="exclude"
                allTags={allTags}
              />
            ))
          )}
          <Button
            type="secondary"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingIndex(null)
              setEditingCriteria(null)
              setModalListType('exclude')
              setModalOpen(true)
            }}
            sx={{
              borderColor: 'error.main',
              color: 'error.main',
              '&:hover': {
                borderColor: 'error.dark',
                backgroundColor: 'error.50',
              },
            }}
          >
            Add Exclude Criteria
          </Button>
        </Box>
      </Box>

      <CriteriaFormModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false)
          setEditingIndex(null)
          setEditingCriteria(null)
        }}
        listType={modalListType}
        onAdd={handleAdd}
        onEdit={handleEditCriteria}
        editingCriteria={editingCriteria}
        availableTags={allTags}
        currentTagId={tag.id}
      />
    </Box>
  )
}

export default TagCriteriaEditor
